import {useState, useEffect} from "react";
import {Box, Stack, Typography} from "@mui/material";
import {Navigate, useParams, useNavigate} from "react-router";
import {appSession} from "../../../TR3AD.ts";
import WidgetHeader from "../../../widgets/WidgetHeader.tsx";
import {appStyles} from "../../../AppStyles.ts";
import type {TimelineMatrix} from "../../../sdo/TimelineMatrix.ts";

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function daysInMonth(year: number, month: number): number {
    return new Date(year, month, 0).getDate();
}

// TODO: Replace with API call to TimelineManager.getMatrix(year)
function buildTestData(year: number): TimelineMatrix {
    const rows: TimelineMatrix["rows"] = {};
    for (let day = 1; day <= 31; day++) {
        const counts: Record<number, number> = {};
        for (let m = 1; m <= 12; m++) {
            if (day <= daysInMonth(year, m)) {
                counts[m] = Math.random() < 0.3 ? Math.floor(Math.random() * 12) + 1 : 0;
            }
        }
        const total = Object.values(counts).reduce((s, c) => s + c, 0);
        if (total > 0) {
            rows[day] = {total, counts};
        }
    }
    return {rows};
}

// --- Cell styles mirroring desktop card-timeline-* CSS ---

const headerCellSx = {
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#02cfee",
    textAlign: "center" as const,
    padding: "6px 4px",
    backgroundColor: "rgba(0, 50, 100, 0.4)",
    borderRadius: "6px",
    userSelect: "none" as const,
};

const monthHeaderSx = {
    ...headerCellSx,
    cursor: "pointer",
    "&:hover": {
        backgroundColor: "rgba(0, 80, 140, 0.6)",
    },
};

const labelCellSx = {
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#F2F5FA",
    textAlign: "center" as const,
    padding: "6px 4px",
    cursor: "pointer",
    borderRadius: "10px",
    "&:hover": {
        color: "#02cfee",
    },
};

const countCellSx = {
    fontSize: "0.65rem",
    fontWeight: 600,
    color: "#38ff82",
    textAlign: "center" as const,
    padding: "5px 3px",
    backgroundColor: "#091920",
    borderRadius: "10px",
    opacity: 0.7,
    cursor: "pointer",
    "&:hover": {
        opacity: 1.0,
    },
};

const zeroCellSx = {
    fontSize: "0.65rem",
    fontWeight: 600,
    color: "#3F464F",
    textAlign: "center" as const,
    padding: "5px 3px",
    backgroundColor: "#091920",
    borderRadius: "10px",
    opacity: 0.7,
    cursor: "default",
};

const emptyCellSx = {
    padding: "5px 3px",
};

function UXTimelineYear() {
    const {networkUID, year: yearParam} = useParams();
    const navigate = useNavigate();
    const year = Number(yearParam);
    const [matrix, setMatrix] = useState<TimelineMatrix | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // TODO: Replace with API call
        const data = buildTestData(year);
        setMatrix(data);
        setLoading(false);
    }, [networkUID, year]);

    return (
        <Stack direction="column" width="100%" minHeight="100vh">
            {!appSession.activeSession() ? (
                <Navigate to="/" />
            ) : (
                <Stack direction="column" spacing={1} width="99%">
                    <WidgetHeader
                        title={`Timeline - ${year}`}
                        breadcrumbs={[
                            {display: "Networks", linkTo: "/networks/", activeView: false},
                            {display: String(networkUID), linkTo: `/networks/${networkUID}`, activeView: false},
                            {display: "Timeline", linkTo: `/networks/${networkUID}/timeline`, activeView: false},
                            {display: String(year), linkTo: "", activeView: true},
                        ]}
                    />
                    <Stack sx={appStyles.sectionBox} spacing={0} width="100%" height="100%" alignItems="start">
                        {loading ? (
                            <Typography sx={{color: "#38ff82", fontSize: "0.85rem", padding: "16px"}}>Loading...</Typography>
                        ) : (
                            <Box sx={{width: "100%", overflowX: "auto", padding: "8px"}}>
                                <Box sx={{display: "grid", gridTemplateColumns: "50px repeat(12, 1fr)", gap: "3px", minWidth: "700px"}}>
                                    {/* Header row */}
                                    <Typography sx={headerCellSx}>Day</Typography>
                                    {MONTH_SHORT.map((m, mi) => (
                                        <Typography
                                            key={m}
                                            sx={monthHeaderSx}
                                            onClick={() => navigate(`/networks/${networkUID}/timeline/${year}/${mi + 1}`)}
                                        >
                                            {m}
                                        </Typography>
                                    ))}

                                    {/* Day rows 1-31 */}
                                    {Array.from({length: 31}, (_, i) => i + 1).map(day => {
                                        const row = matrix?.rows[day];

                                        const handleDayClick = () => {
                                            for (let m = 1; m <= 12; m++) {
                                                if (day <= daysInMonth(year, m)) {
                                                    navigate(`/networks/${networkUID}/timeline/${year}/${m}/${day}`);
                                                    break;
                                                }
                                            }
                                        };

                                        return [
                                            <Typography
                                                key={`d-${day}`}
                                                sx={labelCellSx}
                                                onClick={handleDayClick}
                                            >
                                                {day}
                                            </Typography>,
                                            ...MONTH_SHORT.map((_, mi) => {
                                                const monthIndex = mi + 1;
                                                const valid = day <= daysInMonth(year, monthIndex);
                                                if (!valid) {
                                                    return <Box key={`e-${day}-${mi}`} sx={emptyCellSx} />;
                                                }
                                                const count = row?.counts[monthIndex] ?? 0;
                                                return (
                                                    <Typography
                                                        key={`c-${day}-${mi}`}
                                                        sx={count > 0 ? countCellSx : zeroCellSx}
                                                        onClick={count > 0 ? () => navigate(`/networks/${networkUID}/timeline/${year}/${monthIndex}/${day}`) : undefined}
                                                    >
                                                        {count}
                                                    </Typography>
                                                );
                                            }),
                                        ];
                                    })}
                                </Box>
                            </Box>
                        )}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}

export default UXTimelineYear;
